import type {
  CamelCased,
  CampaignChartPoint,
  CampaignMetricTotals,
  ChartResponse,
  MetricServer,
} from '@metapic-apps/shared'

import { normalizeMoney } from '@/lib/adapters/common.adapter'
import type { MoneyServerType } from '@/lib/types/common.types'

const CURRENT_SERIES_KEY = 'current'

const toNumericValue = (value: number | MoneyServerType): number =>
  typeof value === 'number' ? value : normalizeMoney(value).amount

/**
 * Picks the `current` series and normalizes each daily point (money values become
 * plain numbers, the per-traffic-source breakdown is flattened) for the line chart.
 */
export const normalizeCampaignChart = (response: ChartResponse): CampaignChartPoint[] => {
  const series = response.series ?? []
  const current = series.find((item) => item.key === CURRENT_SERIES_KEY) ?? series[0]

  return (current?.data ?? []).map((point) => ({
    date: point.date,
    value: toNumericValue(point.value),
    breakdown: (point.breakdown ?? []).map((entry) => ({
      key: entry.key,
      value: toNumericValue(entry.value),
    })),
  }))
}

/**
 * Reduces the `/performance/summary` list into a `{ [metricKey]: total }` map used to
 * populate the metric buttons' totals.
 */
export const normalizeCampaignMetricTotals = (
  metrics: CamelCased<MetricServer>[],
): CampaignMetricTotals =>
  metrics.reduce<CampaignMetricTotals>((acc, metric) => {
    acc[metric.key] = toNumericValue(metric.value)
    return acc
  }, {})
